import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, Send, Trash2, Bot, Sparkles } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import './Chatbot.css';

import api from '../../api';

// Removed API_BASE_URL since api.js handles it

const Chatbot = ({ hidden = false }) => {
  const SUGGESTIONS = [
    "How do I calm interview nerves?",
    "Help me pick a career path",
    "What are the best IT interview questions?"
  ];

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hello! I am your AI assistant. How can I help you today?' }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isLoading) {
      inputRef.current?.focus();
    }
  }, [isOpen, isLoading]);

  const handleClearChat = () => {
    setMessages([
      { role: 'assistant', content: 'Hello! I am your AI assistant. How can I help you today?' }
    ]);
  };

  const playPopSound = () => {
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(800, audioCtx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(1200, audioCtx.currentTime + 0.1);
      
      gainNode.gain.setValueAtTime(0, audioCtx.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.3, audioCtx.currentTime + 0.02);
      gainNode.gain.linearRampToValueAtTime(0, audioCtx.currentTime + 0.1);
      
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      
      oscillator.start();
      oscillator.stop(audioCtx.currentTime + 0.1);
    } catch (e) {
      console.log('Audio not supported');
    }
  };

  const handleToggleChat = (open) => {
    setIsOpen(open);
    if (open) playPopSound();
  };

  const handleSend = async (e, suggestionText = null) => {
    e?.preventDefault();
    const userMessage = suggestionText || input.trim();
    if (!userMessage || isLoading) return;

    setInput('');
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
    
    // Add user message to UI
    const updatedMessages = [...messages, { role: 'user', content: userMessage }];
    setMessages(updatedMessages);
    setIsLoading(true);

    try {
      // Prepare history for API
      const history = updatedMessages.slice(0, -1).map(m => ({ role: m.role, content: m.content }));
      
      const response = await api.post('/ai-agents/chat', {
          message: userMessage,
          history: history
      });

      const data = response.data;
      
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
      playPopSound();
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I am having trouble connecting to the server right now. Please try again later.' }]);
    } finally {
      setIsLoading(false);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  };

  return (
    <div className="chatbot-container" style={{ display: hidden ? 'none' : 'block' }}>
      {isOpen && (
        <div className="chatbot-window">
          <div className="chatbot-header">
            <div className="chatbot-header-left">
              <div className="chatbot-header-icon">
                <Bot size={22} />
              </div>
              <div className="chatbot-header-text">
                <h3 style={{ color: '#ffffff', margin: 0, fontWeight: 800, fontSize: '1.05rem', letterSpacing: '-0.01em' }}>EdVergenceX Assistant</h3>
                <span className="chatbot-status" style={{ color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', fontWeight: 600, marginTop: '2px' }}><span className="status-dot"></span> Online & Ready</span>
              </div>
            </div>
            <div className="chatbot-header-right">
              <button className="chatbot-action-btn" onClick={handleClearChat} title="Clear Chat History">
                <Trash2 size={16} />
              </button>
              <button className="chatbot-action-btn close" onClick={() => setIsOpen(false)} title="Minimize Assistant">
                <X size={18} />
              </button>
            </div>
          </div>
          
          <div className="chatbot-messages">
            {messages.map((msg, index) => (
              <div key={index} className={`message-wrapper ${msg.role}`}>
                {msg.role === 'assistant' && (
                  <div className="chatbot-avatar-circle" title="EdVergenceX AI">
                    <Bot size={20} />
                  </div>
                )}
                <div className={`message ${msg.role}`}>
                  {msg.role === 'assistant' ? (
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                  ) : (
                    msg.content
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="message-wrapper assistant">
                <div className="chatbot-avatar-circle">
                  <Bot size={20} />
                </div>
                <div className="typing-indicator">
                  <span></span><span></span><span></span>
                </div>
              </div>
            )}
            
            {messages.length === 1 && !isLoading && (
              <div className="chatbot-suggestions">
                <span className="suggestions-label"><Sparkles size={14} /> Suggested questions:</span>
                {SUGGESTIONS.map((sug, idx) => (
                  <button key={idx} className="suggestion-pill" onClick={(e) => handleSend(e, sug)}>
                    {sug}
                  </button>
                ))}
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          <form className="chatbot-input-container" onSubmit={handleSend}>
            <input
              ref={inputRef}
              type="text"
              className="chatbot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask a question..."
            />
            <button type="submit" className="chatbot-send" disabled={!input.trim() || isLoading}>
              <Send size={18} />
            </button>
          </form>
        </div>
      )}

      {!isOpen && (
        <button className="chatbot-button" onClick={() => handleToggleChat(true)} title="Chat with EdVergenceX Assistant">
          <div className="chatbot-ball-icon-wrap">
            <MessageSquare size={26} />
            <Sparkles size={14} className="chatbot-ball-sparkle" />
          </div>
          <span className="chatbot-ball-badge"></span>
        </button>
      )}
    </div>
  );
};

export default Chatbot;
